import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

/**
 * Review-invitation continuation.
 *
 * Task 4 requires an invited review to belong to a signed-in account that
 * matches the invitation, so `/review/:token` now has to be able to send a
 * visitor through authentication and get them back. This module owns how the
 * review token survives that trip.
 *
 * It deliberately mirrors the conventions already settled in
 * `src/lib/orgInvitations.ts`, and for the same reason: what travels is an
 * OPAQUE TOKEN, never a destination URL. Every path here is reconstructed from
 * a hardcoded template, so no value that has been outside the app's hands can
 * become a navigation target and there is no open redirect to reach.
 *
 * The review token already appears in the emailed `/review/:token` link, so
 * carrying it on an internal callback path adds no exposure that the invitation
 * email did not already have.
 */

/**
 * `org_review_invites.token` is `uuid NOT NULL DEFAULT gen_random_uuid()`, so a
 * real token is always a uuid. Anything else cannot be redeemed — PostgREST
 * rejects it against the RPC's uuid parameter before the function body runs —
 * and is discarded here rather than turned into a doomed visit.
 */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The query parameter that carries a review token on an internal auth path. */
export const REVIEW_INVITE_PARAM = "review_token";

/** Where the same-browser fallback copy lives. */
export const REVIEW_TOKEN_STORAGE_KEY = "compaire_pending_review_token";

/**
 * The storage copy is a SAME-BROWSER convenience and never the only carrier: an
 * emailed confirmation or reset link may well be opened somewhere else, which
 * is exactly why the token also rides the callback URL. Kept short because its
 * only job is to survive one authentication, not to be a durable record.
 */
export const REVIEW_TOKEN_MAX_AGE_MS = 30 * 60 * 1000;

export function isReviewToken(token: string | null | undefined): token is string {
  return Boolean(token && UUID_PATTERN.test(token));
}

/**
 * The review page path, built from a hardcoded template. Returns null for
 * anything that is not a well-formed token, so a malformed or absent value
 * cannot become a destination.
 */
export function reviewPathFromToken(
  token: string | null | undefined
): string | null {
  if (!isReviewToken(token)) return null;
  return `/review/${encodeURIComponent(token)}`;
}

/** The review token on a query string, or null when absent or malformed. */
export function readReviewToken(search: string): string | null {
  const token = new URLSearchParams(search).get(REVIEW_INVITE_PARAM);
  return isReviewToken(token) ? token : null;
}

/**
 * The `emailRedirectTo` for a sign-up or magic link started from a review page.
 *
 * The PATH is a constant. Only the token rides along, and only when it is a
 * well-formed one — the same shape as `signupEmailRedirectUrl` in
 * orgInvitations.ts, which is the convention this follows.
 */
export function reviewSignupRedirectUrl(
  origin: string,
  reviewToken?: string | null
): string {
  const callback = `${origin}/auth/callback`;
  if (!isReviewToken(reviewToken)) return callback;
  return `${callback}?${REVIEW_INVITE_PARAM}=${encodeURIComponent(reviewToken)}`;
}

/** The `redirectTo` for a password reset started from a review page. */
export function reviewResetPasswordUrl(
  origin: string,
  reviewToken?: string | null
): string {
  const reset = `${origin}/reset-password`;
  if (!isReviewToken(reviewToken)) return reset;
  return `${reset}?${REVIEW_INVITE_PARAM}=${encodeURIComponent(reviewToken)}`;
}

/**
 * Where an authenticated visitor should land when a review invitation was
 * waiting on the URL they came back on, or null to fall through to normal
 * role-based routing.
 */
export function reviewCallbackRedirect(search: string): string | null {
  return reviewPathFromToken(readReviewToken(search));
}

interface StoredReviewToken {
  token?: string;
  ts?: number;
}

/** Same-browser fallback. Never throws: storage can be unavailable or full. */
export function storeReviewToken(token: string, now: number): void {
  if (!isReviewToken(token)) return;
  try {
    localStorage.setItem(
      REVIEW_TOKEN_STORAGE_KEY,
      JSON.stringify({ token, ts: now } satisfies StoredReviewToken)
    );
  } catch {
    /* storage unavailable — the URL carrier is the one that matters */
  }
}

/**
 * Parse a stored token, honouring only a fresh, well-formed one. A malformed,
 * stale or absent value is indistinguishable from nothing being stored.
 */
export function parseStoredReviewToken(
  raw: string | null | undefined,
  now: number
): string | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const stored = parsed as StoredReviewToken;
  if (typeof stored.ts !== "number") return null;
  if (now - stored.ts >= REVIEW_TOKEN_MAX_AGE_MS) return null;
  return isReviewToken(stored.token) ? stored.token : null;
}

export function readStoredReviewToken(now: number): string | null {
  try {
    return parseStoredReviewToken(
      localStorage.getItem(REVIEW_TOKEN_STORAGE_KEY),
      now
    );
  } catch {
    return null;
  }
}

export function clearStoredReviewToken(): void {
  try {
    localStorage.removeItem(REVIEW_TOKEN_STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * The server-derived state of an invitation for THIS caller, as returned by
 * `get_review_invite_by_token`. The page branches on this and never recomputes
 * eligibility in the browser.
 *
 * There is deliberately no `invalid` member: a well-formed uuid matching no
 * invitation returns no row, and a malformed one is rejected by PostgREST
 * before the function runs. Both are handled as the absence of a state.
 */
export type ReviewViewerState =
  | "consumed"
  | "expired"
  | "authentication_required"
  | "wrong_account"
  | "already_reviewed"
  | "eligible";

const VIEWER_STATES: readonly ReviewViewerState[] = [
  "consumed",
  "expired",
  "authentication_required",
  "wrong_account",
  "already_reviewed",
  "eligible",
];

/**
 * Narrow an unknown value to a viewer state. An unrecognised value is treated
 * as no state at all, which the page renders as the generic invalid-link
 * experience — the conservative direction, since the alternative is showing a
 * form we cannot prove the caller is entitled to.
 */
export function asReviewViewerState(value: unknown): ReviewViewerState | null {
  return typeof value === "string" &&
    (VIEWER_STATES as readonly string[]).includes(value)
    ? (value as ReviewViewerState)
    : null;
}

/**
 * The two review RPCs, typed from the generated schema.
 *
 * These aliases are not a second description of the contract — they resolve
 * through `Database["public"]["Functions"]`, which is generated from the
 * hosted schema. The database remains the single source of truth for both
 * shapes, and a change to either function reaches this module as a type error
 * rather than being absorbed silently.
 *
 * They are named and exported so callers can import a meaningful type instead
 * of spelling out a deep index into the generated file at each point of use.
 */
type ReviewFunctions = Database["public"]["Functions"];

/**
 * The nine arguments `submit_review_invite` accepts.
 *
 * There is deliberately no `p_reviewer_user_id` and no `p_is_verified`, and
 * that absence is now the schema's rather than this file's: the server derives
 * the reviewer from `auth.uid()` and leaves verification to the integrity
 * trigger, so a caller has nothing to assert about its own identity.
 */
export type SubmitReviewInviteArgs =
  ReviewFunctions["submit_review_invite"]["Args"];

/**
 * One row of `get_review_invite_by_token`.
 *
 * `viewer_state` arrives typed as `string`, which is correct rather than a
 * shortcoming: the server decides it and nothing on this side has validated
 * it. Callers narrow it through `asReviewViewerState`, which fails closed on
 * anything unrecognised. A pre-narrowed type here would let an unknown value
 * flow into a page that believes its switch is exhaustive.
 *
 * `agent_email` is absent because the function no longer returns it, and
 * `personal_message` is present because it still does, even though nothing
 * renders it.
 */
export type ReviewInviteRow =
  ReviewFunctions["get_review_invite_by_token"]["Returns"][number];

/** What supabase-js hands back. Narrower than PostgrestError on purpose. */
export interface RpcError {
  message: string;
}

/**
 * Look up an invitation and the caller's server-derived state for it.
 *
 * Returns at most one row — the function is `LIMIT 1` — so the array is
 * collapsed here rather than at every call site. A well-formed token matching
 * no invitation yields `{ row: null, error: null }`, which is not an error
 * condition: it is the server saying the invitation does not exist.
 *
 * Never throws. supabase-js normally resolves with an `error`, but a
 * transport-level failure (offline, DNS, aborted fetch) rejects instead, and an
 * unhandled rejection would leave the review page spinning. Normalised the same
 * way `acceptOrgInvitation` normalises its own.
 */
export async function fetchReviewInvite(
  token: string
): Promise<{ row: ReviewInviteRow | null; error: RpcError | null }> {
  if (!isReviewToken(token)) {
    return { row: null, error: { message: "review_invite_invalid" } };
  }

  try {
    const { data, error } = await supabase.rpc("get_review_invite_by_token", {
      p_token: token,
    });
    if (error) return { row: null, error };
    return { row: data?.[0] ?? null, error: null };
  } catch (err) {
    return { row: null, error: { message: normalizeRpcMessage(err) } };
  }
}

/**
 * Submit an invited review.
 *
 * The arguments are passed through verbatim — no defaulting, no reshaping, no
 * derived fields. Whatever the server decides about eligibility is returned as
 * an error whose `message` is one of the stable identifiers; this function does
 * not interpret them, and `reviewSubmitErrorMessage` maps them to copy.
 */
export async function submitReviewInvite(
  args: SubmitReviewInviteArgs
): Promise<{ reviewId: string | null; error: RpcError | null }> {
  try {
    const { data, error } = await supabase.rpc("submit_review_invite", args);
    if (error) return { reviewId: null, error };
    return { reviewId: data ?? null, error: null };
  } catch (err) {
    return { reviewId: null, error: { message: normalizeRpcMessage(err) } };
  }
}

function normalizeRpcMessage(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message?: unknown }).message ?? "");
  }
  return "";
}

/**
 * The stable error identifiers raised by `submit_review_invite`. They arrive as
 * the Postgres exception MESSAGE, so they can be matched exactly rather than
 * sniffed out of prose.
 */
export type ReviewSubmitError =
  | "review_invite_not_authenticated"
  | "review_invite_invalid"
  | "review_invite_consumed"
  | "review_invite_expired"
  | "review_invite_wrong_account"
  | "review_already_exists";

export const REVIEW_SUBMIT_FALLBACK_ERROR =
  "Something went wrong. Please try again.";

const SUBMIT_ERROR_COPY: Record<ReviewSubmitError, string> = {
  review_invite_not_authenticated:
    "Please sign in to submit your review.",
  review_invite_invalid:
    "This review link is invalid or has expired.",
  review_invite_consumed:
    "This review has already been submitted. Thank you!",
  review_invite_expired:
    "This review link has expired.",
  // Never names the invited address. The caller already controls the account
  // they are signed in to, so "not this one" tells them nothing they could not
  // determine by trying; the invited address would tell them something new.
  review_invite_wrong_account:
    "This invitation was sent to a different account.",
  review_already_exists:
    "You have already reviewed this organization.",
};

/** Copy for a submission failure, falling back to the generic message. */
export function reviewSubmitErrorMessage(
  message: string | null | undefined
): string {
  if (message && message in SUBMIT_ERROR_COPY) {
    return SUBMIT_ERROR_COPY[message as ReviewSubmitError];
  }
  return REVIEW_SUBMIT_FALLBACK_ERROR;
}
